import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import {
    TextField, Button, Select, MenuItem, IconButton, Typography,
    Grid,
    FormControl,
    InputLabel,
    Box,
    OutlinedInput,
    InputAdornment
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AddIcon from '@mui/icons-material/Add';
import { Snackbar, Alert } from '@mui/material';
import axios from 'axios';
import { UserContext } from '../../../UserContext';

const getInvoiceNumber = () => {
    const now = new Date();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const yy = String(now.getFullYear()).slice(-2);
    return `AC/${mm}${dd}/${yy}/`;
};

const gstMapping = {
    'Website Development': 18,
    'Mobile App Development': 12,
    'Consulting': 5
};

const InvoiceDrawer = ({ onSave, editId }) => {
    const [buyer, setBuyer] = useState({
        name: '', mobile: '', email: '', address: '',
    });
      const { role, user_id } = useContext(UserContext);
    const [productData, setProductData] = useState([]);
    const [paymentTerms, setPaymentTerms] = useState(['50%', '70%', '80%']);
    const [selectedPayment, setSelectedPayment] = useState('50%');
    const [newPaymentOption, setNewPaymentOption] = useState('');
    const [itemsData, setItemsData] = useState([]);
    const [selectedItemId, setSelectedItemId] = useState('');

    const [product, setProduct] = useState(null);
    const [descriptions, setDescriptions] = useState([{ text: '', amount: 0 }]);

    const invoicePrefix = getInvoiceNumber();
    const [invoiceSuffix, setInvoiceSuffix] = useState('');
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [showAddInput, setShowAddInput] = useState(false);
    const [newTermValue, setNewTermValue] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [productsResponse, servicesResponse] = await Promise.all([
                    axios.get("/api/products/"),
                    axios.get("/api/services/")
                ]);

                const products = productsResponse.data.map(p => ({
                    id: p.id,
                    name: p.product_name,
                    type: 'product'
                }));

                const services = servicesResponse.data.map(s => ({
                    id: s.id,
                    name: s.service_name,
                    type: 'service'
                }));

                const combinedItems = [...products, ...services];
                setItemsData(combinedItems);

            } catch (error) {
                console.error("Fetch error:", error);
            }
        };

        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const product = await axios.get("/api/products/");
            setProductData(product.data);
        } catch (error) {
            console.error("Fetch error:", error);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const loadExistingInvoice = async (id) => {
        try {
            const res = await axios.get(`/api/invoice/?id=${id}`);
            const data = res.data;

            setInvoiceSuffix(data.invoice_no.split("/").pop()); // set last token of invoice_no
            setBuyer({
                name: data.buyer_name,
                mobile: data.mobile,
                email: data.email,
                address: data.address,
                buyer_accountno: data?.buyer_accountno,
                buyer_ifsccode: data?.buyer_ifsccode,
                buyer_bankname: data?.buyer_bankname,
            });
            setSelectedPayment(data.payment_term);
            setSelectedItemId(data.product?.id || '');
            setDescriptions(data.descriptions);
        } catch (error) {
            console.error("Failed to load invoice:", error);
        }
    };

    useEffect(() => {
        if (editId) {
            loadExistingInvoice(editId);
        } else {
            resetForm();
        }
    }, [editId]);


    const grandTotal = useMemo(() => {
        const total = descriptions.reduce((acc, cur) => acc + parseFloat(cur.amount || 0), 0);
        const gst = gstMapping[product] || 0;
        return (total + (total * (gst / 100))).toFixed(2);
    }, [descriptions, product]);


    const resetForm = () => {
        setInvoiceSuffix('');
        setBuyer({ name: '', mobile: '', email: '', address: '', buyer_bankname: '', buyer_ifsccode: '', buyer_accountno: '' });
        setSelectedPayment('50%');
        setSelectedItemId('');
        setDescriptions([{ text: '', amount: 0 }]);
        setNewPaymentOption('');
    };


    const addDescriptionField = () => {
        setDescriptions([...descriptions, { text: '', amount: 0 }]);
    };

    const handleDescriptionChange = (index, key, value) => {
        const updated = [...descriptions];
        updated[index][key] = value;
        setDescriptions(updated);
    };

    const handleAddNewPaymentTerm = () => {
        if (newPaymentOption && !paymentTerms.includes(`${newPaymentOption}%`)) {
            setPaymentTerms([...paymentTerms, `${newPaymentOption}%`]);
            setSelectedPayment(`${newPaymentOption}%`);
            setNewPaymentOption('');
        }
    };

    console.log(buyer, "buyer")
    const handleSubmit = useCallback(async () => {
        // if (
        //     !invoiceSuffix ||
        //     !buyer.name || !buyer.mobile || !buyer.email || !buyer.address || !buyer.buyer_accountno || !buyer.buyer_ifsccode || !buyer.buyer_bankname ||
        //     !selectedPayment || !product ||
        //     descriptions.some(d => !d.text || !d.amount)
        // ) {
        //     setSnackbarMessage("Please fill all required fields.");
        //     setOpenSnackbar(true);
        //     return;
        // }

        const invoiceData = {
            invoice_no: `${invoicePrefix}${invoiceSuffix}`,
            buyer_name: buyer.name,
            mobile: buyer.mobile,
            email: buyer.email,
            address: buyer.address,
            product_id: selectedItemId,
            payment_term: selectedPayment,
            date: new Date().toISOString().split('T')[0],
            descriptions,
            grand_total: grandTotal,
            created_by: user_id,
        };

        try {
            await onSave(invoiceData, editId);
        } catch (error) {
            console.error(error);
            setSnackbarMessage("Failed to save invoice.");
            setOpenSnackbar(true);
        }
    }, [invoiceSuffix, buyer, selectedPayment, selectedItemId, descriptions, grandTotal, editId, onSave]);

    const handleInputChange = useCallback((e) => {
        const { name, files } = e.target;
        setBuyer((prev) => ({ ...prev, [name]: e.target.value }));
    }, []);

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16 }}>
                    <TextField
                        label="Invoice Prefix"
                        value={invoicePrefix}
                        disabled
                        sx={{ flex: 1 }}
                    />
                    <TextField
                        label="Invoice Suffix"
                        value={invoiceSuffix}
                        onChange={(e) => setInvoiceSuffix(e.target.value)}
                        placeholder="Enter ID"
                        sx={{ flex: 1 }}
                    />
                </div>
            </Grid>

            <Grid item xs={12}>
                <TextField
                    fullWidth
                    label="Buyer Name"
                    value={buyer.name}
                    onChange={(e) => setBuyer({ ...buyer, name: e.target.value })}
                    variant="outlined"
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    fullWidth
                    label="Mobile Number"
                    value={buyer.mobile}
                    onChange={(e) => setBuyer({ ...buyer, mobile: e.target.value })}
                    variant="outlined"
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    fullWidth
                    label="Email"
                    value={buyer.email}
                    onChange={(e) => setBuyer({ ...buyer, email: e.target.value })}
                    variant="outlined"
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    fullWidth
                    label="Address"
                    value={buyer.address}
                    onChange={(e) => setBuyer({ ...buyer, address: e.target.value })}
                    variant="outlined"
                    multiline rows={2}
                />
            </Grid>

            <Grid item xs={12}>
                <FormControl fullWidth variant="outlined">
                    <InputLabel>Payment Terms</InputLabel>
                    <Select
                        value={selectedPayment}
                        onChange={(e) => {
                            setSelectedPayment(e.target.value);
                            setShowAddInput(false);
                        }}
                        label="Payment Terms"
                        input={
                            <OutlinedInput
                                endAdornment={
                                    <InputAdornment position="end">
                                        <AddCircleOutlineIcon
                                            onClick={() => setShowAddInput(prev => !prev)}
                                            sx={{ cursor: "pointer", color: "primary.main", "&:hover": { color: "primary.dark" } }}
                                        />
                                    </InputAdornment>
                                }
                            />
                        }
                    >
                        {paymentTerms.map((term) => (
                            <MenuItem key={term} value={term}>
                                {term}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>

            {showAddInput && (
                <Grid item xs={12} display="flex" gap={1}>
                    <TextField
                        label="Enter New %"
                        type="number"
                        value={newTermValue}
                        onChange={(e) => setNewTermValue(e.target.value)}
                    />
                    <Button
                        variant="contained"
                        onClick={() => {
                            const formatted = `${newTermValue}%`;
                            if (!newTermValue) {
                                setSnackbarMessage("Please enter a percentage.");
                                setOpenSnackbar(true);
                                return;
                            }
                            if (paymentTerms.includes(formatted)) {
                                setSnackbarMessage(`"${formatted}" already exists.`);
                                setOpenSnackbar(true);
                            } else {
                                setPaymentTerms(prev => [...prev, formatted]);
                                setSelectedPayment(formatted);
                            }
                            setShowAddInput(false);
                            setNewTermValue('');
                        }}
                    >
                        Add
                    </Button>
                </Grid>
            )}

        <Grid item xs={12}>
            <FormControl fullWidth variant="outlined">
                <InputLabel>Product / Service Name</InputLabel>
                <Select
                    value={selectedItemId}
                    onChange={(e) => setSelectedItemId(e.target.value)}
                    label="Product / Service Name"
                >
                    {itemsData.map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                            {item.type === 'product' ? `Product: ${item.name}` : `Service: ${item.name}`}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Grid>
            <Grid item xs={12}>
                {/* <Typography style={{ marginTop: 10 }}>Description + Amount</Typography> */}
                {
                    descriptions.map((desc, index) => (
                        <div key={index} style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                            <TextField
                                label="Description"
                                fullWidth
                                value={desc.text}
                                onChange={(e) => handleDescriptionChange(index, 'text', e.target.value)}
                            />
                            <TextField
                                label="Amount"
                                type="number"
                                value={desc.amount}
                                onChange={(e) => handleDescriptionChange(index, 'amount', e.target.value)}
                            />
                        </div>
                    ))
                }
            </Grid>
            <Grid item xs={12}>
                <Button startIcon={<AddIcon />} onClick={addDescriptionField}>
                    Add More
                </Button>
            </Grid>
            <Grid item xs={12}>
                <Typography variant="h6" style={{ marginTop: 20 }}>
                    Grand Total: ₹{grandTotal}
                </Typography>
            </Grid>
            <Grid item xs={12} display="flex" justifyContent="center" mt={1}>
               <Button
    type="button"
    variant="contained"
    fullWidth
    onClick={handleSubmit}
    sx={{
        backgroundColor: '#fb7102', // Your custom orange color
        color: 'white',             // Ensures text is readable
        '&:hover': {
            backgroundColor: '#e59002', // A slightly darker shade for hover
        },
    }}
>
    Save Invoice
</Button>
            </Grid>
            <Snackbar
                open={openSnackbar}
                autoHideDuration={3000}
                onClose={() => setOpenSnackbar(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={() => setOpenSnackbar(false)} severity="error" sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>

        </Grid>
    );
};

export default InvoiceDrawer;
